

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewOrderCouponModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        travel_order_id: travelOrderId || '',
        coupon_id: '',
        discount_amount: '',
        customer_id: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [travelOrders, setTravelOrders] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!travelOrderId) {
                fetchTravelOrders();
            }
            fetchCoupons();
            fetchCustomers();
        }
    }, [isOpen, travelOrderId]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchCoupons = async () => {
        setLoadingCoupons(true);
        try {
            const response = await axiosInstance.get('/coupons/');
            setCoupons(response.data || []);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoadingCoupons(false);
        }
    };

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Coupon Name is required';
        if (!formData.travel_order_id) errors.travel_order_id = 'Travel Order is required';
        if (!formData.coupon_id) errors.coupon_id = 'Coupon is required';
        if (!formData.discount_amount) errors.discount_amount = 'Discount Amount is required';
        if (!formData.customer_id) errors.customer_id = 'Customer is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                travel_order_id: parseInt(formData.travel_order_id),
                coupon_id: parseInt(formData.coupon_id),
                discount_amount: parseFloat(formData.discount_amount),
                customer_id: parseInt(formData.customer_id),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                travel_order_id: travelOrderId || '',
                coupon_id: '',
                discount_amount: '',
                customer_id: ''
            });
        } catch (error) {
            console.error('Error saving Order Coupon:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Order Coupon',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Order Coupon</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Coupon Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_order_id} required>
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.travel_order_id && <MDTypography variant="caption" color="error">{formErrors.travel_order_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.coupon_id} required>
                                        <InputLabel>Coupon</InputLabel>
                                        <Select
                                            name="coupon_id"
                                            value={formData.coupon_id}
                                            onChange={handleInputChange}
                                            label="Coupon"

                                            disabled={loadingCoupons}
                                        >
                                            <MenuItem value=""><em>Select a Coupon</em></MenuItem>
                                            {coupons.map(c => (
                                                <MenuItem key={c.id} value={c.id}>{c.name} ({c.code})</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.coupon_id && <MDTypography variant="caption" color="error">{formErrors.coupon_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Discount Amount"
                                        name="discount_amount"
                                        type="number"
                                        value={formData.discount_amount}
                                        onChange={handleInputChange}
                                        error={!!formErrors.discount_amount}
                                        helperText={formErrors.discount_amount}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.customer_id} required>
                                        <InputLabel>Customer</InputLabel>
                                        <Select
                                            name="customer_id"
                                            value={formData.customer_id}
                                            onChange={handleInputChange}
                                            label="Customer"

                                            disabled={loadingCustomers}
                                        >
                                            <MenuItem value=""><em>Select a Customer</em></MenuItem>
                                            {customers.map(cust => (
                                                <MenuItem key={cust.id} value={cust.id}>{cust.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.customer_id && <MDTypography variant="caption" color="error">{formErrors.customer_id}</MDTypography>}
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Order Coupon'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewOrderCouponModal;

